export type ShortcutKey =
  | { kind: 'key'; key: string }
  | { kind: 'mouse'; button: 'mouseBack' | 'mouseForward' };

export const SHORTCUT_IDS = [
  'startProxy',
  'stopProxy',
  'toggleIntercept',
  'clearHistory',
  'sendReplay',
  'toggleDecoder',
  'applyOperation',
  'clearInput',
  'closeDecoder',
  'globalSearch',
  'nextTab',
  'previousTab',
  'nextTabAlt',
  'previousTabAlt',
  'goBack',
  'goForward',
  'closeTab',
  'settings',
  'find',
  'mouseGoBack',
  'mouseGoForward',
  'developerTools',
  'reload',
  'toggleFullscreen',
  'zoomIn',
  'zoomOut',
  'resetZoom',
] as const;

export type ShortcutId = (typeof SHORTCUT_IDS)[number];

export type ShortcutCategoryId =
  | 'proxyControl'
  | 'replay'
  | 'decoder'
  | 'navigation'
  | 'mouseGestures'
  | 'window';

export type Shortcut = {
  id: ShortcutId;
  keys: ShortcutKey[];
  hasDescription: boolean;
};

export type ShortcutCategory = {
  id: ShortcutCategoryId;
  shortcuts: Shortcut[];
};

const key = (value: string): ShortcutKey => ({ kind: 'key', key: value });

const shortcut = ({
  id,
  keys,
  hasDescription = false,
}: {
  id: ShortcutId;
  keys: ShortcutKey[];
  hasDescription?: boolean;
}): Shortcut => ({ id, keys, hasDescription });

export const buildShortcutCategories = ({
  isMac,
}: {
  isMac: boolean;
}): ShortcutCategory[] => {
  const mod = key(isMac ? 'Cmd' : 'Ctrl');

  return [
    {
      id: 'proxyControl',
      shortcuts: [
        shortcut({ id: 'startProxy', keys: [mod, key('Shift'), key('P')] }),
        shortcut({ id: 'stopProxy', keys: [mod, key('Shift'), key('S')] }),
        shortcut({ id: 'toggleIntercept', keys: [mod, key('I')] }),
        shortcut({
          id: 'clearHistory',
          keys: [mod, key('Shift'), key('Delete')],
        }),
      ],
    },
    {
      id: 'replay',
      shortcuts: [
        shortcut({
          id: 'sendReplay',
          keys: [mod, key('R')],
          hasDescription: true,
        }),
      ],
    },
    {
      id: 'decoder',
      shortcuts: [
        shortcut({
          id: 'toggleDecoder',
          keys: [mod, key('D')],
          hasDescription: true,
        }),
        shortcut({
          id: 'applyOperation',
          keys: [mod, key('Enter')],
          hasDescription: true,
        }),
        shortcut({
          id: 'clearInput',
          keys: [mod, key('K')],
          hasDescription: true,
        }),
        shortcut({
          id: 'closeDecoder',
          keys: [key('Escape')],
          hasDescription: true,
        }),
      ],
    },
    {
      id: 'navigation',
      shortcuts: [
        shortcut({
          id: 'globalSearch',
          keys: [mod, key('Shift'), key('F')],
          hasDescription: true,
        }),
        shortcut({
          id: 'nextTab',
          keys: [key('Ctrl'), key('Tab')],
          hasDescription: true,
        }),
        shortcut({
          id: 'previousTab',
          keys: [key('Ctrl'), key('Shift'), key('Tab')],
          hasDescription: true,
        }),
        shortcut({
          id: 'nextTabAlt',
          keys: [mod, key('PageDown')],
          hasDescription: true,
        }),
        shortcut({
          id: 'previousTabAlt',
          keys: [mod, key('PageUp')],
          hasDescription: true,
        }),
        shortcut({
          id: 'goBack',
          keys: isMac ? [mod, key('[')] : [key('Alt'), key('←')],
          hasDescription: true,
        }),
        shortcut({
          id: 'goForward',
          keys: isMac ? [mod, key(']')] : [key('Alt'), key('→')],
          hasDescription: true,
        }),
        shortcut({
          id: 'closeTab',
          keys: [mod, key('W')],
          hasDescription: true,
        }),
        shortcut({ id: 'settings', keys: [mod, key(',')] }),
        shortcut({ id: 'find', keys: [mod, key('F')], hasDescription: true }),
      ],
    },
    {
      id: 'mouseGestures',
      shortcuts: [
        shortcut({
          id: 'mouseGoBack',
          keys: [{ kind: 'mouse', button: 'mouseBack' }],
          hasDescription: true,
        }),
        shortcut({
          id: 'mouseGoForward',
          keys: [{ kind: 'mouse', button: 'mouseForward' }],
          hasDescription: true,
        }),
      ],
    },
    {
      id: 'window',
      shortcuts: [
        shortcut({ id: 'developerTools', keys: [key('F12')] }),
        shortcut({ id: 'reload', keys: [mod, key('Shift'), key('R')] }),
        shortcut({
          id: 'toggleFullscreen',
          keys: isMac ? [key('Ctrl'), key('Cmd'), key('F')] : [key('F11')],
        }),
        shortcut({ id: 'zoomIn', keys: [mod, key('+')] }),
        shortcut({ id: 'zoomOut', keys: [mod, key('-')] }),
        shortcut({ id: 'resetZoom', keys: [mod, key('0')] }),
      ],
    },
  ];
};
